import { useBoardSettingsStore } from "@/stores/boardSettingsStore";
import { useGameStore } from "@/stores/gameStore";
import { Star } from "lucide-react";
import React from "react";
import { Confetti } from "../Confetti";
import { Button } from "./button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "./dialog";

const LEVELS = [
  {
    label: "Beginner",
    stars: 1,
    color: "#6ee7b733",
    skillLevel: 1,
    elo: 500,
    icon: "🧢",
    description: "Makes frequent mistakes, easy win!",
  },
  {
    label: "Intermediate",
    stars: 2,
    color: "#fde68a33",
    skillLevel: 4,
    elo: 800,
    icon: "🎓",
    description: "Plays like a casual club player",
  },
  {
    label: "Advanced",
    stars: 3,
    color: "#60a5fa33",
    skillLevel: 10,
    elo: 1500,
    icon: "🎩",
    description: "Solid amateur, strong club player",
  },
  {
    label: "Grandmaster",
    stars: 4,
    color: "#f472b633",
    skillLevel: 20,
    elo: 2500,
    icon: "🏆",
    description: "World-class, full engine strength!",
  },
];

export const Difficulty: React.FC = () => {
  const aiSkillLevel = useGameStore((s) => s.aiSkillLevel);
  const aiElo = useGameStore((s) => s.aiElo);
  const setAiSkillLevel = useGameStore((s) => s.setAiSkillLevel);
  const setAiElo = useGameStore((s) => s.setAiElo);
  const darkMode = useBoardSettingsStore((s) => s.darkMode);

  // Set both skillLevel and elo in zustand
  const handleSelect = (level: (typeof LEVELS)[0]) => {
    setAiSkillLevel(level.skillLevel);
    setAiElo(level.elo);
  };

  return (
    <div className="flex flex-col items-center justify-center w-full h-full">
      <div className="max-w-4xl w-full bg-white/5 backdrop-blur-sm rounded-lg shadow-lg p-6 sm:p-20 flex flex-col h-full">
        <h2 className="text-3xl font-bold mb-6 text-center">
          Choose Difficulty
        </h2>
        <div className="flex-1 overflow-y-auto min-h-0 px-2 py-20">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-20 max-w-3xl mx-auto pb-4">
            {LEVELS.map((level) => {
              const isSelected =
                aiSkillLevel === level.skillLevel && aiElo === level.elo;
              const baseBg = isSelected
                ? level.color
                : darkMode
                  ? "#232946"
                  : "#f3f4f6";
              const baseText = darkMode ? "#e5e7eb" : "#222";
              const border = isSelected
                ? `2px solid ${level.color}`
                : darkMode
                  ? "2px solid #232946"
                  : "2px solid #f3f4f6";
              return (
                <Button
                  key={level.label}
                  className="w-full h-full"
                  variant="outline"
                  onClick={() => handleSelect(level)}
                  style={{
                    background: baseBg,
                    color: baseText,
                    border,
                    boxShadow: isSelected
                      ? `0 0 12px ${level.color}`
                      : undefined,
                    position: "relative",
                    width: "100%",
                    minHeight: 180,
                    fontWeight: "bold",
                    fontSize: 18,
                    transition: "all 0.2s",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    alignItems: "center",
                    padding: "1.5rem 1rem",
                  }}
                >
                  <div className="flex flex-col items-center gap-3 w-full">
                    <div className="flex gap-1">
                      {Array.from({ length: level.stars }).map((_, i) => (
                        <span key={i} style={{ fontSize: "1.25rem" }}>
                          ⭐
                        </span>
                      ))}
                    </div>
                    <span style={{ fontSize: "2.5rem" }}>{level.icon}</span>
                    <span className="text-lg sm:text-xl font-bold">
                      {level.label}
                    </span>
                    <span
                      className="text-xs sm:text-sm mt-1 max-w-[200px] text-center"
                      style={{ color: darkMode ? "#cbd5e1" : "#555" }}
                    >
                      {level.description}
                    </span>
                    <span
                      className="text-xs sm:text-sm mt-1 max-w-[200px] text-center font-mono"
                      style={{ color: darkMode ? "#a3e635" : "#2563eb" }}
                    >
                      ELO: {level.elo}
                    </span>
                  </div>
                  {isSelected && level.label === "Grandmaster" && (
                    <div style={{ position: "absolute", top: -10, right: -10 }}>
                      <Confetti />
                    </div>
                  )}
                </Button>
              );
            })}
          </div>
        </div>
        <div className="mt-8 flex justify-center">
          <DialogTrigger asChild>
            <Button
              className="text-lg py-6 px-12 rounded-full font-bold"
              style={{
                background: darkMode ? "#e5e7eb" : "#232946",
                color: darkMode ? "#232946" : "#e5e7eb",
              }}
            >
              Done
            </Button>
          </DialogTrigger>
        </div>
      </div>
    </div>
  );
};

export const DifficultyDialogButton: React.FC<{ buttonClassName?: string }> = ({
  buttonClassName,
}) => {
  const darkMode = useBoardSettingsStore((s) => s.darkMode);

  const baseBg = darkMode ? "#232946" : "#f3f4f6";
  const baseText = darkMode ? "#e5e7eb" : "#222";
  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button variant="outline" className={buttonClassName}>
          <Star className="mr-2 h-4 w-4 text-yellow-400" />
          Difficulty
        </Button>
      </DialogTrigger>
      <DialogContent
        className=" min-w-full w-screen h-screen max-w-none max-h-none sm:rounded-none"
        style={{ background: baseBg, color: baseText }}
      >
        <DialogHeader>
          <DialogTitle>Choose AI Difficulty</DialogTitle>
        </DialogHeader>
        <div className="w-full h-full absolute top-0 left-0">
          <Difficulty />
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default Difficulty;

